import type React from "react";
import { useState } from "react";
import { Clock } from "lucide-react";
import { useSurveyAggregate } from "./useSurvey";
import { survey as surveyNs, insights } from "@duress/shared";
import { Select } from "@/components/ui/select";
import { ErrorCard } from "@/components/ErrorCard";
import { EmptyState } from "@/components/EmptyState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SURVEY_RESULTS_READY } from "./feature-flag";

const SURVEY_QUESTIONS = surveyNs.SURVEY_QUESTIONS;
const INDUSTRIES = insights.INDUSTRIES;

export function SurveyResultsPage(): React.JSX.Element {
  if (!SURVEY_RESULTS_READY) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-head font-semibold">Feedback</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Customer satisfaction across all accounts
          </p>
        </div>
        <EmptyState
          icon={Clock}
          title="Coming soon"
          body="Feedback reporting isn't ready yet — check back soon."
        />
      </div>
    );
  }

  return <SurveyResultsPageContent />;
}

function SurveyResultsPageContent(): React.JSX.Element {
  const [industry, setIndustry] = useState<string | undefined>(undefined);
  const { data, isLoading, error, refetch } = useSurveyAggregate(industry);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-head font-semibold">Feedback</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Customer satisfaction across all accounts
          </p>
        </div>
        <Select
          value={industry ?? ""}
          onChange={(e) => setIndustry(e.currentTarget.value || undefined)}
          aria-label="Filter by industry"
        >
          <option value="">All industries</option>
          {INDUSTRIES.map((ind) => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <LoadingSkeleton variant="kpis" />
      ) : error ? (
        <ErrorCard message="Couldn't load feedback." onRetry={() => void refetch()} />
      ) : data ? (
        <ResultsContent data={data} />
      ) : null}
    </div>
  );
}

function ResultsContent({ data }: { data: import("@duress/shared").survey.SurveyAggregate }): React.JSX.Element {
  const responseRate =
    data.totalSent > 0 ? Math.round((data.totalCompleted / data.totalSent) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Surveys sent" value={String(data.totalSent)} />
        <StatCard
          label="Responses"
          value={`${data.totalCompleted}`}
          sub={`${responseRate}% response rate`}
        />
        {data.totalCompleted === 0 ? (
          <StatCard label="Avg overall score" value="—" sub="No responses yet" />
        ) : (
          <StatCard
            label="Avg overall score"
            value={data.averageScoreOverall !== null ? `${data.averageScoreOverall.toFixed(1)}/5` : "—"}
          />
        )}
      </div>

      {data.totalCompleted === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-sm text-muted-foreground">No completed surveys yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Send surveys from any client's Survey tab.
          </p>
        </div>
      ) : (
        <>
          {/* Per-question averages */}
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-semibold">Average score per question</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Based on {data.totalCompleted} response{data.totalCompleted !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="divide-y divide-border">
              {SURVEY_QUESTIONS.map((question, i) => {
                const score = data.averageScorePerQuestion[i] ?? null;
                return (
                  <div key={i} className="flex items-center gap-4 px-5 py-3">
                    <span className="text-xs text-muted-foreground w-5 shrink-0">{i + 1}</span>
                    <p className="text-sm flex-1">{question}</p>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        {score !== null && (
                          <div
                            className={`h-full rounded-full ${scoreColour(score)}`}
                            style={{ width: `${(score / 5) * 100}%` }}
                          />
                        )}
                      </div>
                      <span className="text-sm font-semibold tabular-nums w-8 text-right">
                        {score !== null ? score.toFixed(1) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent responses */}
          {data.recentResponses.length > 0 && (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="px-5 py-4 border-b border-border">
                <h2 className="font-semibold">Recent responses</h2>
              </div>
              <div className="divide-y divide-border">
                {data.recentResponses.map((r, i) => {
                  const date = new Date(r.completedAt).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <div key={i} className="flex items-center gap-4 px-5 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{r.clientName}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.respondentName ? `${r.respondentName} · ` : ""}{date}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <ScorePill score={r.averageScore} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card px-5 py-4">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="num text-3xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function ScorePill({ score }: { score: number }): React.JSX.Element {
  // text-falcon-green/text-phoenix-orange fail AA contrast on a white card
  // (~2.3:1) — text-pill-green/-orange are theme-tuned readable variants.
  const bg =
    score >= 4 ? "bg-falcon-green/15 text-pill-green"
    : score >= 3 ? "bg-phoenix-orange/15 text-pill-orange"
    : "bg-emergency-red/15 text-pill-red";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${bg}`}>
      {score.toFixed(1)}/5
    </span>
  );
}

function scoreColour(score: number): string {
  if (score >= 4) return "bg-falcon-green";
  if (score >= 3) return "bg-phoenix-orange";
  return "bg-emergency-red";
}
